import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/features/auth/AuthContext'
import type { UserRole } from '@/types/auth'
import { useToast } from '@/hooks/use-toast'
import { useEffect, useRef } from 'react'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: UserRole[]
  requireTenant?: boolean
}

export function ProtectedRoute({ children, allowedRoles, requireTenant = false }: ProtectedRouteProps) {
  const { t } = useTranslation()
  const { user, currentTenant, loading, tenantsLoading, tenantsFetched, hasRole, hasGlobalRole } = useAuth()
  const { toast } = useToast()
  const hasShownToast = useRef(false)

  const isAuthorized = requireTenant ? hasRole(allowedRoles) : hasGlobalRole(allowedRoles)

  useEffect(() => {
    if (!loading && !tenantsLoading && tenantsFetched && user && !isAuthorized && !hasShownToast.current) {
      hasShownToast.current = true
      toast({
        variant: 'destructive',
        title: t('auth.accessDenied'),
        description: t('auth.accessDeniedMessage'),
      })
    }
  }, [loading, tenantsLoading, tenantsFetched, user, isAuthorized, toast, t])

  // Wait for auth and tenants to fully load
  if (loading || tenantsLoading || (user && !tenantsFetched)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Requires tenant but no tenant selected
  if (requireTenant && !currentTenant) {
    return <Navigate to="/" replace />
  }

  // Logged in but not authorized
  if (!isAuthorized) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
